import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { Lock, Mail, AlertCircle, Loader2, Key } from 'lucide-react';

interface LoginPageProps {
  onSwitchToSignup: () => void;
}

export function LoginPage({ onSwitchToSignup }: LoginPageProps) {
  const { user } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasAccessCode, setHasAccessCode] = useState(false);

  // If user is logged in, the parent App component will handle showing the dashboard
  // This component won't unmount until user is set, so keep showing loading state
  useEffect(() => {
    if (user && loading) {
      // User is now logged in, parent will show dashboard
      // Keep loading true so we show the spinner
    }
  }, [user, loading]);

  // Check for valid access code on mount
  useEffect(() => {
    const accessCode = sessionStorage.getItem('valid_access_code');
    const accessCodeEmail = sessionStorage.getItem('access_code_email');

    if (accessCode) {
      setHasAccessCode(true);
      if (accessCodeEmail) {
        setEmail(accessCodeEmail);
      }
    } else {
      // No access code - redirect back to landing
      setError(
        'Access code required. Please enter your access code from the landing page.'
      );
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) {
        setError(signInError.message || 'Failed to sign in. Please check your credentials.');
        setLoading(false);
        return;
      }

      if (!data.session) {
        setError('Sign in failed. Please try again.');
        setLoading(false);
        return;
      }

      // Success! Auth state will update via onAuthStateChange
      // Loading state will be managed by AuthContext
    } catch (err: any) {
      setError(err?.message || 'Unexpected error during sign in');
      setLoading(false);
    }
  };

  return (
    <div className='min-h-screen flex items-center justify-center bg-base'>
      <div className='w-full max-w-md'>
        <div className='card p-8'>
          <div className='text-center mb-8'>
            <div className='inline-flex items-center justify-center w-16 h-16 bg-accent-100 rounded-full mb-4'>
              <Lock className='w-8 h-8 text-accent-500' />
            </div>
            <h1 className='text-3xl font-bold text-primary mb-2'>
              Welcome Back
            </h1>
            <p className='text-secondary'>
              Sign in to your AI Developer Platform
            </p>
          </div>

          {!hasAccessCode && (
            <div className='alert alert-warning mb-6'>
              <Key className='w-5 h-5 flex-shrink-0 mt-0.5' />
              <div>
                <p className='text-sm font-medium mb-1'>
                  Access Code Required
                </p>
                <p className='text-sm'>
                  Please return to the landing page and enter your access code
                  to continue.
                </p>
              </div>
            </div>
          )}

          {error && hasAccessCode && (
            <div
              className='alert alert-error mb-6'
              role='alert'
              aria-live='polite'
            >
              <AlertCircle
                className='w-5 h-5 flex-shrink-0 mt-0.5'
                aria-hidden='true'
              />
              <p
                className='text-sm'
                id='email-error password-error'
              >
                {error}
              </p>
            </div>
          )}

          {/* OAuth Providers */}
          <div className='space-y-3 mb-6'>
            <button
              type='button'
              onClick={async () => {
                setLoading(true);
                const { error } = await supabase.auth.signInWithOAuth({
                  provider: 'github',
                  options: {
                    redirectTo: window.location.origin,
                  },
                });
                if (error) setError(error.message);
                setLoading(false);
              }}
              disabled={loading}
              className='btn btn-outline w-full'
            >
              <svg className='w-5 h-5' fill='currentColor' viewBox='0 0 24 24'>
                <path d='M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z' />
              </svg>
              Continue with GitHub
            </button>

            <button
              type='button'
              onClick={async () => {
                setLoading(true);
                const { error } = await supabase.auth.signInWithOAuth({
                  provider: 'google',
                  options: {
                    redirectTo: window.location.origin,
                  },
                });
                if (error) setError(error.message);
                setLoading(false);
              }}
              disabled={loading}
              className='btn btn-outline w-full'
            >
              <svg className='w-5 h-5' viewBox='0 0 24 24'>
                <path
                  fill='#4285F4'
                  d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z'
                />
                <path
                  fill='#34A853'
                  d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z'
                />
                <path
                  fill='#FBBC05'
                  d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z'
                />
                <path
                  fill='#EA4335'
                  d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z'
                />
              </svg>
              Continue with Google
            </button>
          </div>

          <div className='relative mb-6'>
            <div className='absolute inset-0 flex items-center'>
              <div className='w-full border-t border-default'></div>
            </div>
            <div className='relative flex justify-center text-sm'>
              <span className='px-2 bg-surface text-muted'>
                Or continue with email
              </span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className='space-y-5'>
            <div>
              <label
                htmlFor='email'
                className='block text-sm font-medium text-primary mb-2'
              >
                Email Address
              </label>
              <div className='relative'>
                <Mail className='absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted' />
                <input
                  id='email'
                  type='email'
                  required
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className='input w-full pl-10 pr-4'
                  placeholder='you@example.com'
                  disabled={loading}
                  aria-describedby='email-error'
                  aria-invalid={!!error}
                  aria-required='true'
                />
              </div>
            </div>

            <div>
              <label
                htmlFor='password'
                className='block text-sm font-medium text-primary mb-2'
              >
                Password
              </label>
              <div className='relative'>
                <Lock className='absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted' />
                <input
                  id='password'
                  type='password'
                  required
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className='input w-full pl-10 pr-4'
                  placeholder='Enter your password'
                  disabled={loading}
                  aria-describedby='password-error'
                  aria-invalid={!!error}
                  aria-required='true'
                />
              </div>
            </div>

            <button
              type='submit'
              disabled={loading}
              className='btn btn-primary w-full'
            >
              {loading ? (
                <>
                  <Loader2 className='w-5 h-5 animate-spin' />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          <div className='mt-6 text-center'>
            <p className='text-secondary'>
              Don't have an account?{' '}
              <button
                onClick={onSwitchToSignup}
                className='text-accent-600 hover:text-accent-700 font-semibold transition'
              >
                Sign up for free
              </button>
            </p>
          </div>
        </div>

        <p className='text-center text-sm text-muted mt-6'>
          By signing in, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>
    </div>
  );
}
